import { Repository } from "react3l-common";
import { kebabCase } from "lodash";
import { httpConfig } from "config/http";
import { BASE_API_URL } from "config/consts";
import { Observable } from "rxjs";
import { AxiosResponse } from "axios";
import nameof from "ts-nameof.macro";
import { API_STUDENT_PREFIX } from "config/api-consts";
import { Student, StudentFilter } from "models/Student";
import { Status, StatusFilter } from "models/Status";
import { Subject, SubjectFilter } from "models/Subject";
import { Organization, OrganizationFilter } from "models/Organization";
import { Gender, GenderFilter } from "models/Gender";
import { Major, MajorFilter } from "models/Major";
import { Cohort, CohortFilter } from "models/Cohort";
import { EducationType, EducationTypeFilter } from "models/EducationType";

export type KeyType = string | number;

export class StudentRepository extends Repository {
  constructor() {
    super(httpConfig);
    this.baseURL = new URL(API_STUDENT_PREFIX, BASE_API_URL).href;
  }

  public count = (studentFilter?: StudentFilter): Observable<number> => {
    return this.http
      .post<number>(kebabCase(nameof(this.count)), studentFilter)
      .pipe(Repository.responseDataMapper<number>());
  };

  public list = (studentFilter?: StudentFilter): Observable<Student[]> => {
    return this.http
      .post<Student[]>(kebabCase(nameof(this.list)), studentFilter)
      .pipe(Repository.responseMapToList<Student>(Student));
  };

  public get = (id: number | string): Observable<Student> => {
    return this.http
      .post<Student>(kebabCase(nameof(this.get)), { id })
      .pipe(Repository.responseMapToModel<Student>(Student));
  };

  public create = (student: Student): Observable<Student> => {
    return this.http
      .post<Student>(kebabCase(nameof(this.create)), student)
      .pipe(Repository.responseMapToModel<Student>(Student));
  };

  public update = (student: Student): Observable<Student> => {
    return this.http
      .post<Student>(kebabCase(nameof(this.update)), student)
      .pipe(Repository.responseMapToModel<Student>(Student));
  };

  public delete = (student: Student): Observable<Student> => {
    return this.http
      .post<Student>(kebabCase(nameof(this.delete)), student)
      .pipe(Repository.responseMapToModel<Student>(Student));
  };

  public bulkDelete = (idList: KeyType[]): Observable<void> => {
    return this.http
      .post(kebabCase(nameof(this.bulkDelete)), idList)
      .pipe(Repository.responseDataMapper());
  };

  public save = (student: Student): Observable<Student> => {
    return student.id ? this.update(student) : this.create(student);
  };

  public singleListStatus = (): Observable<Status[]> => {
    return this.http
      .post<Status[]>(
        kebabCase(nameof(this.singleListStatus)),
        new StatusFilter()
      )
      .pipe(Repository.responseMapToList<Status>(Status));
  };

  public filterListStatus = (): Observable<Status[]> => {
    return this.http
      .post<Status[]>(
        kebabCase(nameof(this.filterListStatus)),
        new StatusFilter()
      )
      .pipe(Repository.responseMapToList<Status>(Status));
  };

  public singleListSubject = (
    subjectFilter: SubjectFilter
  ): Observable<Subject[]> => {
    return this.http
      .post<Subject[]>(kebabCase(nameof(this.singleListSubject)), subjectFilter)
      .pipe(Repository.responseMapToList<Subject>(Subject));
  };
  public filterListSubject = (
    subjectFilter: SubjectFilter
  ): Observable<Subject[]> => {
    return this.http
      .post<Subject[]>(kebabCase(nameof(this.filterListSubject)), subjectFilter)
      .pipe(Repository.responseMapToList<Subject>(Subject));
  };

  public singleListGender = (
    genderFilter: GenderFilter
  ): Observable<Gender[]> => {
    return this.http
      .post<Gender[]>(kebabCase(nameof(this.singleListGender)), genderFilter)
      .pipe(Repository.responseMapToList<Gender>(Gender));
  };
  public filterListGender = (
    genderFilter: GenderFilter
  ): Observable<Gender[]> => {
    return this.http
      .post<Gender[]>(kebabCase(nameof(this.filterListGender)), genderFilter)
      .pipe(Repository.responseMapToList<Gender>(Gender));
  };

  public singleListEducationType = (
    educationTypeFilter: EducationTypeFilter
  ): Observable<EducationType[]> => {
    return this.http
      .post<EducationType[]>(kebabCase(nameof(this.singleListEducationType)), educationTypeFilter)
      .pipe(Repository.responseMapToList<EducationType>(EducationType));
  };
  public filterListEducationType = (
    educationTypeFilter: EducationTypeFilter
  ): Observable<EducationType[]> => {
    return this.http
      .post<EducationType[]>(kebabCase(nameof(this.filterListEducationType)), educationTypeFilter)
      .pipe(Repository.responseMapToList<EducationType>(EducationType));
  };

  public singleListMajor = (marjorFilter: MajorFilter): Observable<Major[]> => {
    return this.http
      .post<Major[]>(kebabCase(nameof(this.singleListMajor)), marjorFilter)
      .pipe(Repository.responseMapToList<Major>(Major));
  };
  public filterListMajor = (marjorFilter: MajorFilter): Observable<Major[]> => {
    return this.http
      .post<Major[]>(kebabCase(nameof(this.filterListMajor)), marjorFilter)
      .pipe(Repository.responseMapToList<Major>(Major));
  };

  public singleListCohort = (
    cohortFilter: CohortFilter
  ): Observable<Cohort[]> => {
    return this.http
      .post<Cohort[]>(kebabCase(nameof(this.singleListCohort)), cohortFilter)
      .pipe(Repository.responseMapToList<Cohort>(Cohort));
  };

  public filterListCohort = (
    cohortFilter: CohortFilter
  ): Observable<Cohort[]> => {
    return this.http
      .post<Cohort[]>(kebabCase(nameof(this.filterListCohort)), cohortFilter)
      .pipe(Repository.responseMapToList<Cohort>(Cohort));
  };

  public singleListOrganization = (
    organizationFilter: OrganizationFilter
  ): Observable<Organization[]> => {
    return this.http
      .post<Organization[]>(
        kebabCase(nameof(this.singleListOrganization)),
        organizationFilter
      )
      .pipe(Repository.responseMapToList<Organization>(Organization));
  };
  public filterListOrganization = (
    organizationFilter: OrganizationFilter
  ): Observable<Organization[]> => {
    return this.http
      .post<Organization[]>(
        kebabCase(nameof(this.filterListOrganization)),
        organizationFilter
      )
      .pipe(Repository.responseMapToList<Organization>(Organization));
  };

  public import = (
    file: File,
    name: string = nameof(file)
  ): Observable<void> => {
    const formData: FormData = new FormData();
    formData.append(name, file as Blob);
    return this.http
      .post<void>(kebabCase(nameof(this.import)), formData)
      .pipe(Repository.responseDataMapper<any>());
  };

  public importMapping = (
    file: File,
    name: string = nameof(file)
  ): Observable<void> => {
    const formData: FormData = new FormData();
    formData.append(name, file as Blob);
    return this.http
      .post<void>(kebabCase(nameof(this.importMapping)), formData)
      .pipe(Repository.responseDataMapper<any>());
  };

  public export = (filter: any): Observable<AxiosResponse<any>> => {
    return this.http.post("export", filter, {
      responseType: "arraybuffer",
    });
  };

  public exportTemplate = (): Observable<AxiosResponse<any>> => {
    return this.http.post(
      "export-template",
      {},
      {
        responseType: "arraybuffer",
      }
    );
  };

  public exportTemplateMapping = (): Observable<AxiosResponse<any>> => {
    return this.http.post(
      "export-template-mapping",
      {},
      {
        responseType: "arraybuffer",
      }
    );
  };
}

export const studentRepository = new StudentRepository();
